import { inject, Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { HttpErrorResponse } from '@angular/common/http';
import {
  debounceTime,
  map,
  Observable,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
  tap,
  combineLatest,
} from 'rxjs';
import { JsonApiLoaderService } from './json-api-loader.service';
import { GetEntityPipe, GetRequestPipe } from './+state/get-data-item.pipe';
import { DetailJsonApiQuery, JsonApiQuery } from './json-api.query';
import { isEqual } from '@madeinlune/ngx-operators';
import { FacadeService } from './facade.service';
import { DrupalIdentifier } from '@madeinlune/drupal-types';
import { GetUiEntityPipe } from './+state/get-ui-data-item.pipe';

export interface EntityState<T> {
  entity?: T | undefined;
  loaded: boolean;
  loading?: boolean;
  error?: Error;
  query?: JsonApiQuery;
  queryId?: string;
  errorMessage?: string;
}

const initialState = {
  entity: undefined,
  loaded: false,
  loading: true,
  queryId: undefined,
};

@Injectable()
export class EntityStore<T extends DrupalIdentifier> extends ComponentStore<
  EntityState<T>
> {
  private getUiEntityPipe = inject(GetUiEntityPipe);

  readonly queryId$ = this.select((s) => s.queryId);
  readonly query$ = this.select((s) => s.query);
  readonly entity$ = this.select((s) => s.entity).pipe(
    debounceTime(10),
    isEqual()
  ) as Observable<T>;
  readonly uiEntity$ = this.entity$.pipe(
    switchMap((entity) => {
      return this.getUiEntityPipe.transform(entity?.id);
    })
  ) as Observable<T>;
  readonly loading$ = this.select((s) => s.loading).pipe(
    map((loading) => !!loading)
  );

  readonly loaded$ = this.select((s) => s.loaded);

  readonly errorMessage$ = this.select((s) => s.errorMessage);

  readonly error$ = this.select((s) => s.error);

  readonly richError$ = combineLatest([this.errorMessage$, this.error$]).pipe(
    map(([errorMessage, error]) => {
      const message = errorMessage;
      if (message) {
        const errors = [];
        if (error) {
          errors.push(error);
        }
        return { message, errors };
      }
      return undefined;
    })
  );

  load = this.effect<{ query: DetailJsonApiQuery; entityId: string }>(
    pipe(
      tap((queryInfos: { query: DetailJsonApiQuery; entityId: string }) => {
        const { query, entityId } = queryInfos;
        const queryId = `${query.queryId}-${entityId}`;
        const clonedQuery: DetailJsonApiQuery = {
          ...query,
          queryId,
          id: entityId,
        };
        this.facade.loadDetail(clonedQuery);
        this.patchState(initialState);
        //
        console.log('queryId', queryId);
        this.cancelEntitiesMonitoring();
        this.enableEntitiesMonitoring();
        if (this.destroyEntitiesMonitoring$) {
          this.patchState(
            this.getRequest.transform(queryId).pipe(
              takeUntil(this.destroyEntitiesMonitoring$),
              takeUntil(this.destroy$),
              switchMap((storeQuery) => {
                console.log({ storeQuery });
                if (storeQuery) {
                  if (!storeQuery.error && this.destroyEntitiesMonitoring$) {
                    return this.getUiEntityPipe.transform<T>(entityId).pipe(
                      takeUntil(this.destroyEntitiesMonitoring$),
                      takeUntil(this.destroy$),
                      map((entity) => {
                        return {
                          entity,
                          loaded: !!entity,
                          loading: storeQuery.state !== 'success' || !!!entity,
                        };
                      })
                    );
                  } else {
                    console.log(
                      'storeQuery.options.errorContent.message',
                      storeQuery.options.errorContent.message
                    );
                    return of({
                      errorMessage: storeQuery.options.errorContent.message,
                      loading: storeQuery.state !== 'success',
                      error: storeQuery.error,
                    });
                  }
                }
                return of({});
              })
            )
          );
        }
      })
    )
  );

  private destroyEntitiesMonitoring$: Subject<void> | undefined;

  constructor(
    private jsonApiLoaderService: JsonApiLoaderService,
    private getEntityPipe: GetEntityPipe,
    private getRequest: GetRequestPipe,
    private facade: FacadeService
  ) {
    super(initialState);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.cancelEntitiesMonitoring();
  }

  private enableEntitiesMonitoring() {
    this.destroyEntitiesMonitoring$ = new Subject<void>();
  }

  private cancelEntitiesMonitoring() {
    if (this.destroyEntitiesMonitoring$) {
      this.destroyEntitiesMonitoring$.next();
      this.destroyEntitiesMonitoring$.complete();
    }
  }

  reset() {
    this.patchState(initialState);
  }

  get entity() {
    return this.get((s) => s.entity);
  }
}
